/**
 * Smakota — global front-end entry (deferred, ~1 KB).
 *
 * Importing the global stylesheet here makes webpack emit build/theme.css
 * (printed inline by Modules\Assets).
 *
 * Handles only cross-cutting UI:
 *  - mobile navigation panel (aria-expanded, scroll lock, Escape),
 *  - section sub-menus as disclosure buttons (keyboard + touch; hover is CSS),
 *  - search <details>: Escape closes it, focus moves into the field on open,
 *  - back-to-top button,
 *  - saved / recently viewed recipes, share, instant search (engage.js).
 */
import '../scss/main.scss';
import initEngage from './engage';
import initMotion from './motion';
import initGuides from './guides';
import initSeasonal from './seasonal';

( function () {
	'use strict';

	const nav = document.querySelector( '.primary-nav' );
	const toggle = nav && nav.querySelector( '.primary-nav__toggle' );

	/* ---------------- Mobile panel ---------------- */

	// The panel opens under the header's real bottom edge. A fixed
	// `top: --header-h` assumed the header starts at the top of the screen;
	// with the countdown strip (or the admin bar) above it, the panel slid
	// over the lower half of the logo and the buttons.
	function placePanel() {
		const header = document.querySelector( '.site-header' );
		if ( header ) {
			const bottom = Math.max( 0, header.getBoundingClientRect().bottom );
			nav.style.setProperty( '--nav-top', bottom.toFixed( 1 ) + 'px' );
		}
	}

	function setPanel( open ) {
		if ( open ) {
			placePanel();
		}
		toggle.setAttribute( 'aria-expanded', String( open ) );
		nav.setAttribute( 'data-open', String( open ) );
		document.documentElement.classList.toggle( 'has-open-nav', open );
	}

	if ( toggle ) {
		toggle.addEventListener( 'click', () => {
			setPanel( toggle.getAttribute( 'aria-expanded' ) !== 'true' );
		} );
		window.addEventListener( 'resize', () => {
			if ( toggle.getAttribute( 'aria-expanded' ) === 'true' ) {
				placePanel();
			}
		} );

		// Leaving mobile layout closes the panel.
		window
			.matchMedia( '(min-width: 1280px)' )
			.addEventListener( 'change', ( e ) => {
				if ( e.matches ) {
					setPanel( false );
				}
			} );
	}

	/* ---------------- Sub-menus ---------------- */

	const subToggles = nav
		? nav.querySelectorAll( '.primary-nav__sub-toggle' )
		: [];

	function closeSubs( except ) {
		subToggles.forEach( ( btn ) => {
			if ( btn !== except ) {
				btn.setAttribute( 'aria-expanded', 'false' );
				const sub = document.getElementById(
					btn.getAttribute( 'aria-controls' )
				);
				if ( sub ) {
					sub.removeAttribute( 'data-open' );
				}
			}
		} );
	}

	subToggles.forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			const open = btn.getAttribute( 'aria-expanded' ) !== 'true';
			const sub = document.getElementById(
				btn.getAttribute( 'aria-controls' )
			);
			closeSubs( btn );
			btn.setAttribute( 'aria-expanded', String( open ) );
			if ( sub ) {
				sub.toggleAttribute( 'data-open', open );
				if ( open ) {
					sub.setAttribute( 'data-open', 'true' );
				}
			}
		} );
	} );

	document.addEventListener( 'click', ( e ) => {
		if ( nav && ! nav.contains( e.target ) ) {
			closeSubs();
		}
		const search = document.querySelector( '.header-search[open]' );
		if ( search && ! search.contains( e.target ) ) {
			search.removeAttribute( 'open' );
		}
		const langMenu = document.querySelector( '.lang-menu[open]' );
		if ( langMenu && ! langMenu.contains( e.target ) ) {
			langMenu.removeAttribute( 'open' );
		}
	} );

	/* ---------------- Escape ---------------- */

	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key !== 'Escape' ) {
			return;
		}

		const openSub =
			nav &&
			nav.querySelector(
				'.primary-nav__sub-toggle[aria-expanded="true"]'
			);
		if ( openSub ) {
			closeSubs();
			openSub.focus();
			return;
		}

		const search = document.querySelector( '.header-search[open]' );
		if ( search ) {
			search.removeAttribute( 'open' );
			search.querySelector( 'summary' ).focus();
			return;
		}

		const langMenu = document.querySelector( '.lang-menu[open]' );
		if ( langMenu ) {
			langMenu.removeAttribute( 'open' );
			langMenu.querySelector( 'summary' ).focus();
			return;
		}

		if ( toggle && toggle.getAttribute( 'aria-expanded' ) === 'true' ) {
			setPanel( false );
			toggle.focus();
		}
	} );

	/* ---------------- Search disclosure ---------------- */

	const search = document.querySelector( '.header-search' );
	if ( search ) {
		search.addEventListener( 'toggle', () => {
			if ( search.open ) {
				const field = search.querySelector( 'input[type="search"]' );
				if ( field ) {
					field.focus();
				}
			}
		} );
	}

	/* ---------------- Back to top ---------------- */

	const toTop = document.querySelector( '[data-to-top]' );
	if ( toTop ) {
		const reduced = window.matchMedia( '(prefers-reduced-motion: reduce)' );
		let ticking = false;

		// Shown once the reader is ~1.5 screens down; JS-only, so it starts hidden.
		const update = () => {
			ticking = false;
			toTop.classList.toggle(
				'is-visible',
				window.scrollY > window.innerHeight * 1.5
			);
		};

		toTop.hidden = false;
		update();
		window.addEventListener(
			'scroll',
			() => {
				if ( ! ticking ) {
					ticking = true;
					window.requestAnimationFrame( update );
				}
			},
			{ passive: true }
		);

		toTop.addEventListener( 'click', ( e ) => {
			window.scrollTo( {
				top: 0,
				behavior: reduced.matches ? 'auto' : 'smooth',
			} );
			// Keyboard users continue from the top of the page, not the footer.
			// Only them: a mouse or a finger has no place in the tab order to
			// keep, and focus moved by script after a click still draws the
			// keyboard ring round the logo. A click from a key has detail 0.
			const brand = document.querySelector( '.site-header__brand' );
			if ( brand && 0 === e.detail ) {
				brand.focus( { preventScroll: true } );
			}
		} );
	}

	initEngage();
	initMotion();
	initGuides();
	initSeasonal();
} )();
